'use client'

import type { RowSelectionState } from '@tanstack/react-table'
import { memo, useEffect, useMemo, useState } from 'react'
import { useDebouncedValue } from '@tanstack/react-pacer'
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table'
import { ArrowUpDown, CheckCheck, ChevronDown, HardDrive, Search } from 'lucide-react'

import type { RouterOutputs } from '@package/api'
import {
  Button,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  TableBody,
  TableCell,
  Table as TableComponent,
  TableHead,
  TableHeader,
  TableRow,
  toast,
} from '@ui/web/components'
import { typographyVariants } from '@ui/web/lib'
import cn from '@config/tailwind/cn'

import type { ScoreboardType } from '../index'
import { useSharedTRPC } from '../../../../context'
import { useColumns } from './columns'

type ScoreboardRow = RouterOutputs['lesson']['getScoreboardData']['items'][number]

type StatusFilter = 'NOT_STARTED' | 'IN_PROGRESS' | 'NEEDS_GRADING' | 'COMPLETED'
type SortOption = 'NAME_ASC' | 'STARTED_FIRST' | 'FINISHED_FIRST' | 'MOST_POINTS'

type MainTableProps = ScoreboardType & {
  lessonId: string | null
  lessonType: string | null
}

function ScoreboardSearchInput({
  value,
  onChange,
  disabled,
}: {
  value: string
  onChange: (v: string) => void
  disabled: boolean
}) {
  return (
    <InputGroup className='srd:max-w-[280px]'>
      <InputGroupAddon>
        <Search className='srd:size-4' />
      </InputGroupAddon>
      <InputGroupInput
        disabled={disabled}
        placeholder='Cari nama murid...'
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </InputGroup>
  )
}

interface FilterSortBarProps {
  statusFilter: StatusFilter | undefined
  sort: SortOption
  isInClass: boolean
  disabled: boolean
  onStatusFilterChange: (v: StatusFilter | undefined) => void
  onSortChange: (v: SortOption) => void
}

function FilterSortBar({
  statusFilter,
  sort,
  isInClass,
  disabled,
  onStatusFilterChange,
  onSortChange,
}: FilterSortBarProps) {
  const filterOptions = isInClass
    ? [
        { value: '', label: 'Semua' },
        { value: 'NOT_STARTED', label: 'Belum mulai' },
        { value: 'IN_PROGRESS', label: 'Belum selesai' },
        { value: 'NEEDS_GRADING', label: 'Perlu dinilai' },
        { value: 'COMPLETED', label: 'Selesai' },
      ]
    : [
        { value: '', label: 'Semua' },
        { value: 'NOT_STARTED', label: 'Belum mulai' },
        { value: 'COMPLETED', label: 'Selesai' },
      ]

  return (
    <>
      <Select
        value={statusFilter ?? ''}
        onValueChange={(v) => onStatusFilterChange((v || undefined) as StatusFilter | undefined)}
      >
        <SelectTrigger disabled={disabled} className='srd:w-fit srd:min-w-[120px]'>
          <SelectValue placeholder='Semua'>
            {filterOptions.find((o) => o.value === (statusFilter ?? ''))?.label ?? 'Semua'}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {filterOptions.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select value={sort} onValueChange={(v) => onSortChange(v as SortOption)}>
        <SelectTrigger disabled={disabled} className='srd:w-fit srd:min-w-[140px]'>
          <ArrowUpDown className='srd:size-4 srd:mr-1 srd:shrink-0' />
          <SelectValue>
            {sort === 'NAME_ASC'
              ? 'Nama A-Z'
              : sort === 'STARTED_FIRST'
                ? 'Mulai tercepat'
                : sort === 'FINISHED_FIRST'
                  ? 'Selesai tercepat'
                  : 'Poin terbanyak'}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          <SelectItem value='NAME_ASC'>Nama A-Z</SelectItem>
          <SelectItem value='STARTED_FIRST'>Mulai tercepat</SelectItem>
          <SelectItem value='FINISHED_FIRST'>Selesai tercepat</SelectItem>
          <SelectItem value='MOST_POINTS'>Poin terbanyak</SelectItem>
        </SelectContent>
      </Select>
    </>
  )
}

interface BulkActionBarProps {
  selectedCount: number
  onMarkComplete: () => void
  isPending: boolean
  onClear: () => void
  disabled?: boolean
}

function BulkActionBar({ selectedCount, onMarkComplete, isPending, onClear, disabled }: BulkActionBarProps) {
  if (selectedCount === 0) return null

  return (
    <div className='srd:flex srd:items-center srd:gap-x-3 srd:px-4 srd:py-2 srd:bg-navy-10 srd:rounded-lg srd:border srd:border-navy-20'>
      <p className={cn(typographyVariants({ variant: 'label-md' }))}>{selectedCount} murid dipilih</p>

      <Button size='sm' variant='outline' loading={isPending} disabled={isPending || disabled} onClick={onMarkComplete}>
        <CheckCheck className='srd:size-4 srd:mr-1' />
        Tandai selesai
      </Button>

      <Button size='sm' variant='ghost' disabled={isPending} onClick={onClear}>
        Batalkan pilihan
      </Button>
    </div>
  )
}

interface ScoreboardTableProps {
  allRows: ScoreboardRow[]
  columns: ReturnType<typeof useColumns>
  isPending: boolean
  isError: boolean
  isFetchingNextPage: boolean
  hasNextPage: boolean | undefined
  pageSize: number
  rowSelection: RowSelectionState
  onRowSelectionChange: React.Dispatch<React.SetStateAction<RowSelectionState>>
  refetch: () => void
  fetchNextPage: () => void
}

const ScoreboardTable = memo(function ScoreboardTable({
  allRows,
  columns,
  isPending,
  isError,
  isFetchingNextPage,
  hasNextPage,
  pageSize,
  rowSelection,
  onRowSelectionChange,
  refetch,
  fetchNextPage,
}: ScoreboardTableProps) {
  const table = useReactTable({
    data: allRows,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.id,
    enableRowSelection: true,
    state: { rowSelection },
    onRowSelectionChange,
  })

  if (isError) {
    return (
      <div className='srd:flex srd:flex-col srd:items-center srd:justify-center srd:gap-2 srd:py-8 srd:w-full srd:border-outline srd:overflow-hidden srd:rounded-md srd:border'>
        <p className='srd:text-error'>Gagal memuat data papan penilaian</p>
        <Button size='sm' onClick={() => refetch()} variant='outline'>
          Coba lagi
        </Button>
      </div>
    )
  }

  if (isPending) {
    return (
      <div className='srd:w-full srd:border-outline srd:overflow-hidden srd:rounded-md srd:border'>
        <TableComponent>
          <TableHeader className='srd:bg-grey-100/25'>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id} className='srd:text-nowrap'>
                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody className='srd:bg-neutral-white'>
            {new Array(pageSize).fill(0).map((_, i) => (
              <TableRow key={i}>
                {columns.map((column) => (
                  <TableCell key={column.id}>
                    <Skeleton className='srd:h-6 srd:w-full' />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </TableComponent>
      </div>
    )
  }

  if (allRows.length === 0) {
    return (
      <div className='srd:w-full srd:border-outline srd:overflow-hidden srd:rounded-md srd:border'>
        <TableComponent>
          <TableHeader className='srd:bg-grey-100/25'>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id} className='srd:text-nowrap'>
                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody className='srd:bg-neutral-white'>
            <TableRow>
              <TableCell colSpan={columns.length} className='srd:text-center srd:py-8'>
                <div className='srd:flex srd:flex-col srd:justify-center srd:items-center srd:gap-2'>
                  <HardDrive />
                  <p>Tidak ada data</p>
                </div>
              </TableCell>
            </TableRow>
          </TableBody>
        </TableComponent>
      </div>
    )
  }

  return (
    <div className='srd:w-full srd:border-outline srd:overflow-hidden srd:rounded-md srd:border'>
      <TableComponent>
        <TableHeader className='srd:bg-grey-100/25'>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead key={header.id} className='srd:text-nowrap'>
                  {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody className='srd:bg-neutral-white'>
          {table.getRowModel().rows.map((row) => (
            <TableRow key={row.id} data-state={row.getIsSelected() ? 'selected' : undefined}>
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
              ))}
            </TableRow>
          ))}

          {hasNextPage && (
            <TableRow>
              <TableCell colSpan={columns.length} className='srd:text-center srd:py-4'>
                <Button size='sm' onClick={() => void fetchNextPage()} disabled={isFetchingNextPage}>
                  {isFetchingNextPage ? 'Memuat...' : 'Tampilkan lebih banyak'}
                  <ChevronDown className='srd:size-4 srd:ml-2' />
                </Button>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </TableComponent>
    </div>
  )
})

export function MainTable({ lessonId, lessonType, ...p }: MainTableProps) {
  const trpc = useSharedTRPC()
  const queryClient = useQueryClient()

  const [pageSize] = useState(20)
  const [searchValue, setSearchValue] = useState('')
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
  const [statusFilter, setStatusFilter] = useState<StatusFilter | undefined>(undefined)
  const [sort, setSort] = useState<SortOption>('NAME_ASC')

  const [debouncedSearch] = useDebouncedValue(searchValue, { wait: 800 })

  const isInClass = lessonType === 'IN_CLASS'
  const isQuiz = lessonType === 'QUIZ'

  // Reset filter when lesson changes
  useEffect(() => {
    setStatusFilter(undefined)
    setRowSelection({})
  }, [lessonId])

  const input = useMemo(
    () => ({
      courseId: p.type === 'course' ? p.courseId : undefined,
      challengeId: p.type === 'challenge' ? p.challengeId : undefined,
      lessonId: lessonId ?? '',
      limit: pageSize,
      search: debouncedSearch.trim() || undefined,
      statusFilter,
      sort,
    }),
    [p, lessonId, pageSize, debouncedSearch, statusFilter, sort],
  )

  const { data, isPending, isError, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery(
    trpc.lesson.getScoreboardData.infiniteQueryOptions(input, {
      enabled: !!lessonId,
      getNextPageParam: (lastPage) => lastPage.nextCursor,
      initialCursor: 1,
    }),
  )

  const allRows = useMemo(() => data?.pages.flatMap((page) => page.items) ?? [], [data])

  const invalidateScoreboard = async () => {
    await queryClient.invalidateQueries({
      queryKey: trpc.lesson.getScoreboardData.infiniteQueryKey(),
    })
  }

  const columns = useColumns({ p, lessonId, isInClass, isQuiz, onInvalidateScoreboard: invalidateScoreboard })

  const selectedRowIds = useMemo(() => Object.keys(rowSelection).filter((id) => rowSelection[id]), [rowSelection])

  const selectedLearnProgressIds = useMemo(
    () =>
      selectedRowIds.flatMap((enrollmentId) => {
        const row = allRows.find((r) => r.id === enrollmentId)
        return row?.learnProgress?.id ? [row.learnProgress.id] : []
      }),
    [selectedRowIds, allRows],
  )

  const bulkCompleteMutation = useMutation(
    trpc.lesson.editLearnProgress.mutationOptions({
      onSuccess: async () => {
        toast.success(`${selectedLearnProgressIds.length} murid berhasil ditandai selesai`)
        setRowSelection({})
        await queryClient.invalidateQueries({
          queryKey: trpc.lesson.getScoreboardData.infiniteQueryKey(),
        })
      },
      onError: () => {
        toast.error('Gagal menandai selesai')
      },
    }),
  )

  const controlsDisabled = !lessonId || isPending || isError

  return (
    <main className='srd:w-full srd:pt-10 srd:bg-grey-20 srd:min-h-[80dvh] srd:h-fit srd:pb-20'>
      <div className='srd:w-full srd:max-w-[1440px] srd:mx-auto srd:px-4'>
        <div className='srd:flex srd:flex-wrap srd:items-center srd:gap-3 srd:mb-4'>
          <ScoreboardSearchInput value={searchValue} onChange={setSearchValue} disabled={controlsDisabled} />

          <FilterSortBar
            statusFilter={statusFilter}
            sort={sort}
            isInClass={isInClass}
            disabled={controlsDisabled}
            onStatusFilterChange={setStatusFilter}
            onSortChange={setSort}
          />

          {isInClass && (
            <BulkActionBar
              selectedCount={selectedRowIds.length}
              isPending={bulkCompleteMutation.isPending}
              disabled={!lessonId || selectedLearnProgressIds.length === 0}
              onMarkComplete={() =>
                bulkCompleteMutation.mutate({
                  learnProgressIds: selectedLearnProgressIds,
                  data: { status: 'COMPLETED' },
                })
              }
              onClear={() => setRowSelection({})}
            />
          )}
        </div>

        <ScoreboardTable
          allRows={allRows}
          columns={columns}
          isPending={!lessonId || isPending}
          isError={isError}
          isFetchingNextPage={isFetchingNextPage}
          hasNextPage={hasNextPage}
          pageSize={pageSize}
          rowSelection={rowSelection}
          onRowSelectionChange={setRowSelection}
          refetch={refetch}
          fetchNextPage={fetchNextPage}
        />
      </div>
    </main>
  )
}
